"use client";

import type { CSSProperties } from "react";
import { GENRE_COLORS } from "@/lib/genre-palette";
import { getNovelFormatLabel, normalizeNovelGenre } from "@/lib/novel-labels";
import styles from "./cover-mini.module.css";
import { useUiTexts } from "@/lib/ui-texts-client";

/** パイプ区切りのジャンル文字列から最初のジャンルを取り出す */
export function primaryGenre(genre: string): string {
  return normalizeNovelGenre(genre.split("|")[0].trim() || genre);
}

/** 表紙の地の色。属性の色（genre-palette）から、大きく出す本（BookReveal）の表紙と同じ塗り方で作る */
export function coverBackground(genre: string): CSSProperties {
  const key = genre === "scifi" ? "sf" : genre;
  const c = GENRE_COLORS[key as keyof typeof GENRE_COLORS] ?? "#a8a3c2";
  return {
    background: `linear-gradient(160deg, color-mix(in srgb, ${c} 58%, #14101f), color-mix(in srgb, ${c} 24%, #0b0912))`,
  };
}

/**
 * 小さな本の表紙（書庫のカードと読了後の画面で同じものを出す）。
 * 管理画面で属性ごとの本のアイコン（ui-icons の book_<属性> / book_default）があればそれを、無ければ紋と書式を描く。
 */
export default function BookCoverMini({
  genre,
  format,
  iconUrl,
  dogEar = false,
}: {
  genre: string;
  format?: string;
  iconUrl?: string;
  /** 右上の角を折る（書庫のまだ開いていない本の印） */
  dogEar?: boolean;
}) {
  const { t } = useUiTexts();
  const cover = (
    <div aria-hidden="true" className={`${styles.cover} ${dogEar ? styles.dogEared : ""}`} style={coverBackground(primaryGenre(genre))}>
      {iconUrl ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={iconUrl} alt="" />
      ) : (
        <span className={styles.coverOrnament}>
          <svg width="24" height="28" viewBox="0 0 24 28" fill="none" stroke="currentColor" strokeWidth=".9">
            <path d="M12 2 21 14 12 26 3 14Z" />
            <path d="M12 7v14M7 14h10" />
            <circle cx="12" cy="14" r="4" />
          </svg>
          <small>{getNovelFormatLabel(format) || t("shelf.book.formatFallback")}</small>
        </span>
      )}
      {/* 折れて見える頁の裏（切り落とした角の内側の三角） */}
      {dogEar && <span className={styles.dogEarFlap} />}
    </div>
  );
  // 角を切り落とすと外側の影も切れるので、角折りの本は外から影を付け直す
  return dogEar ? <div className={styles.dogEarWrap}>{cover}</div> : cover;
}
